import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Building2,
  CheckCircle2,
  Star,
  Users,
  Shield,
  ArrowRight,
  Send,
  Loader2,
  FileText,
  Phone,
  Mail,
  Globe,
  MapPin,
  Award,
  Briefcase,
  Target,
  Sparkles,
  BadgeCheck,
  Clock,
  Calendar
} from 'lucide-react';

const ProviderPortalPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    // Company Info
    company_name: '',
    business_type: '',
    rfc: '',
    years_experience: '',
    
    // Contact Info
    contact_name: '',
    contact_position: '',
    email: '',
    phone: '',
    website: '',
    
    // Location
    city: '',
    state: '',
    address: '',
    
    // Services
    category: '',
    services_description: '',
    coverage_area: '',
    
    // Additional
    why_join: '',
    certifications: '',
    references: ''
  });

  const categories = [
    { id: 'legal', name: 'Servicios Legales', icon: FileText },
    { id: 'financiero', name: 'Servicios Financieros', icon: Briefcase },
    { id: 'marketing', name: 'Marketing y Publicidad', icon: Target },
    { id: 'tecnologia', name: 'Tecnología', icon: Globe },
    { id: 'construccion', name: 'Construcción y Remodelación', icon: Building2 },
    { id: 'valuacion', name: 'Valuación', icon: Award },
    { id: 'seguros', name: 'Seguros', icon: Shield },
    { id: 'capacitacion', name: 'Capacitación', icon: Users },
    { id: 'otros', name: 'Otros Servicios', icon: Star }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/provider-portal/apply', formData);
      setSubmitted(true);
      toast({
        title: "¡Solicitud enviada!",
        description: "Revisaremos tu información y te contactaremos pronto."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar la solicitud",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  if (submitted) {
    return (
      <Layout>
        <section className="min-h-screen py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl p-12 shadow-xl">
              <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                ¡Solicitud Enviada Exitosamente!
              </h1>
              <p className="text-gray-600 mb-8">
                Hemos recibido tu solicitud para unirte como Proveedor Certificado UPIM. 
                Nuestro equipo revisará tu información y te contactaremos en las próximas 
                48-72 horas hábiles.
              </p>
              <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
                <h3 className="font-semibold text-gray-900 mb-3">Próximos pasos:</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="h-5 w-5 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-xs mr-3 mt-0.5">1</span>
                    Revisión de documentación
                  </li>
                  <li className="flex items-start">
                    <span className="h-5 w-5 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-xs mr-3 mt-0.5">2</span>
                    Verificación de referencias
                  </li>
                  <li className="flex items-start">
                    <span className="h-5 w-5 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-xs mr-3 mt-0.5">3</span>
                    Contacto para entrevista
                  </li>
                  <li className="flex items-start">
                    <span className="h-5 w-5 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-xs mr-3 mt-0.5">4</span>
                    Aprobación y publicación en directorio
                  </li>
                </ul>
              </div>
              <Button 
                onClick={() => navigate('/proveedores')}
                className="bg-gray-900 hover:bg-gray-800"
              >
                Ver Directorio de Proveedores
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-white/10 text-white border border-white/20 px-4 py-1.5">
              <Sparkles className="h-4 w-4 mr-2" />
              Portal de Proveedores
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Conviértete en <span className="text-gray-400">Proveedor Certificado</span> UPIM
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Únete a nuestra red de proveedores verificados y conecta con más de 
              <strong className="text-white"> 5,000+ profesionales inmobiliarios</strong> en todo México.
            </p>
            
            <div className="flex flex-wrap gap-6 text-sm text-gray-400">
              <div className="flex items-center">
                <BadgeCheck className="h-5 w-5 mr-2 text-green-500" />
                Verificación oficial
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                5,000+ afiliados
              </div>
              <div className="flex items-center">
                <Star className="h-5 w-5 mr-2 text-yellow-500" />
                Exposición premium
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-4">
              <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Building2 className="h-6 w-6 text-gray-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Directorio Premium</h3>
              <p className="text-sm text-gray-600">Aparece destacado en nuestro directorio</p>
            </div>
            <div className="text-center p-4">
              <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-gray-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Networking</h3>
              <p className="text-sm text-gray-600">Acceso a eventos exclusivos</p>
            </div>
            <div className="text-center p-4">
              <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <BadgeCheck className="h-6 w-6 text-gray-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Sello de Confianza</h3>
              <p className="text-sm text-gray-600">Verificación UPIM para tus clientes</p>
            </div>
            <div className="text-center p-4">
              <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Target className="h-6 w-6 text-gray-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Leads Calificados</h3>
              <p className="text-sm text-gray-600">Contactos de profesionales activos</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= s ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-20 sm:w-32 h-1 mx-2 ${step > s ? 'bg-gray-900' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Empresa</span>
              <span>Contacto</span>
              <span>Servicios</span>
            </div>
          </div>

          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle className="text-xl">
                {step === 1 && 'Información de la Empresa'}
                {step === 2 && 'Información de Contacto'}
                {step === 3 && 'Servicios y Motivación'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                {/* Step 1: Company Info */}
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Nombre de la Empresa *</Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleInputChange}
                          placeholder="Mi Empresa S.A. de C.V."
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="business_type">Tipo de Negocio *</Label>
                        <Input
                          id="business_type"
                          name="business_type"
                          value={formData.business_type}
                          onChange={handleInputChange}
                          placeholder="S.A., S.C., Persona Física"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="rfc">RFC</Label>
                        <Input
                          id="rfc"
                          name="rfc"
                          value={formData.rfc}
                          onChange={handleInputChange}
                          placeholder="ABC123456XYZ"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="years_experience">Años de Experiencia *</Label>
                        <Input
                          id="years_experience"
                          name="years_experience"
                          type="number"
                          value={formData.years_experience}
                          onChange={handleInputChange}
                          placeholder="5"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">Ciudad *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Ciudad de México"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">Estado *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="CDMX"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address">Dirección</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Calle, Número, Colonia, CP"
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Contact Info */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact_name">Nombre del Contacto *</Label>
                        <Input
                          id="contact_name"
                          name="contact_name"
                          value={formData.contact_name}
                          onChange={handleInputChange}
                          placeholder="Juan Pérez"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_position">Cargo *</Label>
                        <Input
                          id="contact_position"
                          name="contact_position"
                          value={formData.contact_position}
                          onChange={handleInputChange}
                          placeholder="Director Comercial"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Correo Electrónico *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="contacto@empresa.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Teléfono *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="55 1234 5678"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">Sitio Web</Label>
                      <Input
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="https://www.empresa.com"
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Services */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Categoría de Servicio *</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {categories.map(cat => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              formData.category === cat.id 
                                ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <cat.icon className={`h-5 w-5 mb-1 ${formData.category === cat.id ? 'text-gray-900' : 'text-gray-400'}`} />
                            <span className="text-xs font-medium">{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="services_description">Descripción de Servicios *</Label>
                      <Textarea
                        id="services_description"
                        name="services_description"
                        value={formData.services_description}
                        onChange={handleInputChange}
                        placeholder="Describe los servicios que ofreces a profesionales inmobiliarios..."
                        rows={3}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coverage_area">Área de Cobertura *</Label>
                      <Input
                        id="coverage_area"
                        name="coverage_area"
                        value={formData.coverage_area}
                        onChange={handleInputChange}
                        placeholder="Nacional, CDMX y Área Metropolitana, etc."
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="why_join">¿Por qué quieres unirte a UPIM?</Label>
                      <Textarea
                        id="why_join"
                        name="why_join"
                        value={formData.why_join}
                        onChange={handleInputChange}
                        placeholder="Cuéntanos tu motivación para ser parte de nuestra red..."
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="certifications">Certificaciones Relevantes</Label>
                      <Input
                        id="certifications"
                        name="certifications"
                        value={formData.certifications}
                        onChange={handleInputChange}
                        placeholder="ISO 9001, CONOCER, etc."
                      />
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t">
                  {step > 1 ? (
                    <Button type="button" variant="outline" onClick={prevStep}>
                      Anterior
                    </Button>
                  ) : (
                    <div />
                  )}
                  
                  {step < 3 ? (
                    <Button type="button" onClick={nextStep} className="bg-gray-900 hover:bg-gray-800">
                      Siguiente
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button type="submit" className="bg-gray-900 hover:bg-gray-800" disabled={loading}>
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Enviar Solicitud
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="mt-8 bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-gray-500" />
              Proceso de Verificación
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start">
                <span className="h-6 w-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-medium mr-2 flex-shrink-0">1</span>
                <div>
                  <p className="font-medium text-gray-900">Revisión</p>
                  <p className="text-gray-500">24-48 hrs</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="h-6 w-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-medium mr-2 flex-shrink-0">2</span>
                <div>
                  <p className="font-medium text-gray-900">Verificación</p>
                  <p className="text-gray-500">1-3 días</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="h-6 w-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-medium mr-2 flex-shrink-0">3</span>
                <div>
                  <p className="font-medium text-gray-900">Publicación</p>
                  <p className="text-gray-500">Inmediata</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProviderPortalPage;
